import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Mail, Send, CheckCircle2, Eye, MousePointerClick, AlertTriangle,
  XCircle, Ban, UserMinus, Loader2, RefreshCw, Clock, AlertCircle,
} from "lucide-react";

const EVENT_META = {
  sent:         { label: "Envoyés",        color: "#C5A059", icon: Send },
  delivered:    { label: "Délivrés",       color: "#10B981", icon: CheckCircle2 },
  opened:       { label: "Ouverts",        color: "#3B82F6", icon: Eye },
  clicked:      { label: "Cliqués",        color: "#8B5CF6", icon: MousePointerClick },
  soft_bounce:  { label: "Rebonds temp.",  color: "#F59E0B", icon: AlertTriangle },
  hard_bounce:  { label: "Rebonds défin.", color: "#EF4444", icon: XCircle },
  complaint:    { label: "Spams signalés", color: "#DC2626", icon: AlertCircle },
  blocked:      { label: "Bloqués",        color: "#7C2D12", icon: Ban },
  unsubscribed: { label: "Désinscrits",    color: "#64748B", icon: UserMinus },
  deferred:     { label: "Différés",       color: "#A3A3A3", icon: Clock },
  invalid_email:{ label: "Emails invalid.",color: "#DC2626", icon: XCircle },
  error:        { label: "Erreurs",        color: "#EF4444", icon: XCircle },
  mocked:       { label: "Simulés",        color: "#9CA3AF", icon: AlertTriangle },
};

const STATUS_BADGE = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-300",
  opened: "bg-blue-100 text-blue-800 border-blue-300",
  click: "bg-violet-100 text-violet-800 border-violet-300",
  soft_bounce: "bg-amber-100 text-amber-800 border-amber-300",
  hard_bounce: "bg-red-100 text-red-800 border-red-300",
  complaint: "bg-red-200 text-red-900 border-red-400",
  blocked: "bg-stone-200 text-stone-900 border-stone-400",
  mocked: "bg-slate-100 text-slate-600 border-slate-300",
  error: "bg-red-100 text-red-800 border-red-300",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function EmailDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/emails/dashboard", { params: { days } });
      setData(data);
    } catch (err) {
      toast.error("Impossible de charger le tableau de bord");
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>;
  }
  if (!data) return null;
  const t = data.totals;
  const kpis = [
    { k: "sent", v: t.sent },
    { k: "delivered", v: t.delivered },
    { k: "opened", v: t.opened },
    { k: "clicked", v: t.clicked },
    { k: "hard_bounce", v: t.hard_bounce },
    { k: "complaint", v: t.complaint },
  ];
  const total_sent = Math.max(t.sent, 1);
  const deliveryRate = ((t.delivered / total_sent) * 100).toFixed(1);
  const openRate = t.delivered > 0 ? ((t.opened / t.delivered) * 100).toFixed(1) : "0.0";

  return (
    <div data-testid="email-dashboard-page">
      <PageHeader
        title="Tableau de bord Emails"
        subtitle={`Statistiques des ${data.range_days} derniers jours — Brevo ${data.brevo_enabled ? "actif" : "non configuré"}`}
        actions={
          <div className="flex gap-2 items-center">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="email-days-select" className="h-9 px-3 rounded-md border border-slate-200 text-sm">
              <option value={7}>7 jours</option>
              <option value={30}>30 jours</option>
              <option value={90}>90 jours</option>
            </select>
            <Button variant="outline" onClick={load} data-testid="email-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Rafraîchir</Button>
          </div>
        }
      />

      {!data.brevo_enabled && (
        <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-4 text-sm" data-testid="brevo-warning">
          <strong>Brevo non configuré.</strong> Les emails sont simulés. Renseignez <code>BREVO_API_KEY</code> dans <code>backend/.env</code>.
        </div>
      )}

      {/* Webhook URL info */}
      <div className="mb-5 rounded-xl border border-slate-200 bg-gradient-to-r from-[#FDF9F1] to-white p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="brevo-webhook-info">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">URL Webhook Brevo</div>
          <code className="text-xs font-mono bg-white border border-slate-200 px-2 py-1 rounded mt-1 inline-block">
            {`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/brevo`}
          </code>
          <p className="text-xs text-slate-500 mt-2">À copier dans Brevo → Transactional → Settings → Webhooks pour recevoir delivered/opened/clicked/bounce/complaint en temps réel.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => {
          navigator.clipboard?.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/brevo`);
          toast.success("URL copiée");
        }} data-testid="copy-webhook-url">
          Copier
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {kpis.map(({ k, v }) => {
          const m = EVENT_META[k] || EVENT_META.sent;
          const Icon = m.icon;
          return (
            <div key={k} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`email-kpi-${k}`}>
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${m.color}18`, color: m.color }}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-xs text-slate-500">{m.label}</div>
              </div>
              <div className="text-2xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>{v}</div>
            </div>
          );
        })}
      </div>

      {/* Rates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <RatioCard title="Taux de délivrabilité" value={deliveryRate} base={`${t.delivered} / ${t.sent}`} color="#10B981" />
        <RatioCard title="Taux d'ouverture" value={openRate} base={`${t.opened} / ${t.delivered}`} color="#3B82F6" />
      </div>

      {/* Historique */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Mail className="h-4 w-4 text-[#C5A059]" /> Historique des envois ({data.recent.length})
          </h3>
        </div>
        {data.recent.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">Aucun envoi récent.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Destinataire</th>
                  <th className="text-left px-4 py-2">Sujet</th>
                  <th className="text-center px-4 py-2">Statut</th>
                  <th className="text-left px-4 py-2">Dernier événement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.recent.map((n) => {
                  const statusKey = n.status || "pending";
                  const badgeCls = STATUS_BADGE[statusKey] || STATUS_BADGE.pending;
                  const events = n.brevo_events || {};
                  const eventOrder = ["delivered", "opened", "click", "hard_bounce", "soft_bounce", "complaint", "blocked"];
                  return (
                    <tr key={n.id} className="hover:bg-slate-50" data-testid={`email-row-${n.id}`}>
                      <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{n.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="px-4 py-2 font-mono text-xs">{n.to_email}</td>
                      <td className="px-4 py-2 max-w-sm truncate">{n.subject}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`text-[10px] px-2 py-0.5 rounded border inline-block ${badgeCls}`}>{statusKey}</span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1 flex-wrap">
                          {eventOrder.filter(e => events[e]).map(e => {
                            const m = EVENT_META[e === "click" ? "clicked" : e] || EVENT_META.sent;
                            const Icon = m.icon;
                            return (
                              <span key={e} title={`${m.label} — ${events[e]}`} className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded" style={{ backgroundColor: `${m.color}15`, color: m.color }}>
                                <Icon className="h-2.5 w-2.5" />
                              </span>
                            );
                          })}
                          {Object.keys(events).length === 0 && <span className="text-xs text-slate-300">—</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RatioCard({ title, value, base, color }) {
  const pct = Math.min(100, parseFloat(value) || 0);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{title}</div>
        <div className="text-xs text-slate-400">{base}</div>
      </div>
      <div className="mt-2 text-3xl font-bold" style={{ color, fontFamily: "Outfit, sans-serif" }}>{value}%</div>
      <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}
